import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';

const CourseDetails = ({ formData, onFormDataChange }) => {
  const token = localStorage.getItem('token');
  const [coursesData, setCoursesData] = useState({});
  const [courseFeesData, setCourseFeesData] = useState({});
  const [courseDurData, setCourseDurData] = useState({});

  // Fetch courses
  useEffect(() => {
    if (!token) return;
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/course`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const coursesByType = {};
        const fees = {};
        const dur = {};

        res.data.forEach((course) => {
          coursesByType[course.courseType] = coursesByType[course.courseType] || [];
          if (!coursesByType[course.courseType].includes(course.courseName)) {
            coursesByType[course.courseType].push(course.courseName);
          }
          fees[course.courseName] = course.courseFees;
          dur[course.courseName] = course.courseDuration;
        });

        setCoursesData(coursesByType);
        setCourseFeesData(fees);
        setCourseDurData(dur);
      } catch (error) {
        console.error('Error fetching courses:', error);
      }
    };

    fetchCourses();
  }, [token]);

  // Handlers
  const handleCourseTypeChange = (e) => {
    const selectedCourseType = e.target.value;
    onFormDataChange((prev) => ({
      ...prev,
      courseType: selectedCourseType,
      courseName: '',
      courseFees: '',
      courseDuration: '',
    }));
  };

  const handleCourseChange = (selectedOption) => {
    const selectedCourse = selectedOption?.value || '';
    const courseDetails = courseFeesData[selectedCourse] || '';
    const courseDet = courseDurData[selectedCourse] || '';

    onFormDataChange((prev) => ({
      ...prev,
      courseName: selectedCourse,
      courseFees: courseDetails,
      courseDuration: courseDet,
    }));
  };

  // Build dropdown options
  const courseTypeOptions = Object.keys(coursesData);
  const courseOptions =
    formData.courseType && coursesData[formData.courseType]
      ? coursesData[formData.courseType].map((course) => ({
          value: course,
          label: course,
        }))
      : [];

  return (
    <div className="form-section">
      <div className="section-content">
        <div className="form-row">
          {/* Course Type Dropdown */}
          <div className="form-group">
            <label className="label">
              Course Type <span className="mandatory">*</span>
            </label>
            <select
              name="courseType"
              value={formData.courseType || ''}
              onChange={handleCourseTypeChange}
              required
              className="input select"
            >
              <option value="">Select Course Type</option>
              {courseTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          {/* Course Name Searchable Dropdown */}
          <div className="form-group" style={{ minWidth: '250px', marginTop: '4px' }}>
            <label className="label">
              Course Name <span className="mandatory">*</span>
            </label>
            <Select
              options={courseOptions}
              value={
                formData.courseName
                  ? { value: formData.courseName, label: formData.courseName }
                  : null
              }
              onChange={handleCourseChange}
              placeholder={
                formData.courseType ? 'Search or select a course' : 'Please select course type first'
              }
              isDisabled={!formData.courseType}
              isClearable
              classNamePrefix="react-select"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? '#007bff' : '#ccc',
                  boxShadow: state.isFocused ? '0 0 0 1px #007bff' : 'none',
                  '&:hover': { borderColor: '#007bff' },
                  minHeight: '38px',
                  fontFamily: "'Times New Roman', Times, serif",
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? '#6a7178ff' : 'white',
                  color: state.isFocused ? 'white' : 'black',
                  fontFamily: "'Times New Roman', Times, serif",
                }),
              }}
            />
          </div>
        </div>

        {/* Course Start & End Dates */}
        <div className="form-row">
          <div className="form-group">
            <label className="label">Course Start Date</label>
            <input
              type="date"
              value={formData.courseStartDate || ''}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  courseStartDate: e.target.value,
                }))
              }
              className="input"
              max={formData.courseEndDate || ''}
            />
          </div>

          <div className="form-group">
            <label className="label">Course End Date</label>
            <input
              type="date"
              value={formData.courseEndDate || ''}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  courseEndDate: e.target.value,
                }))
              }
              className="input"
              min={formData.courseStartDate || ''}
            />
          </div>
        </div>

        {/* Learning Mode */}
        <div className="form-row">
          <div className="form-group">
            <label className="label">Learning Mode</label>
            <select
              value={formData.learningMode || ''}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  learningMode: e.target.value,
                }))
              }
              className="input select"
            >
              <option value="">Select Learning Mode</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>

          {/* Batch */}
          <div className="form-group">
            <label className="label">Batch</label>
            <select
              value={formData.batch || ''}
              onChange={(e) =>
                onFormDataChange((prev) => ({
                  ...prev,
                  batch: e.target.value,
                }))
              }
              className="input select"
            >
              <option value="">Choose Your Batch</option>
              <option value="9AM-11AM">9AM-11AM</option>
              <option value="12PM-2PM">12PM-2PM</option>
              <option value="3PM-5PM">3PM-5PM</option>
              <option value="6PM-8PM">6PM-8PM</option>
              <option value="Others">Others</option>
            </select>
          </div>
        </div>

        {/* Custom Batch Input */}
        {formData.batch === 'Others' && (
          <div className="form-row">
            <div className="form-group full-width">
              <label className="label">Enter Custom Batch</label>
              <input
                type="text"
                value={formData.customBatch || ''}
                onChange={(e) =>
                  onFormDataChange((prev) => ({
                    ...prev,
                    customBatch: e.target.value,
                  }))
                }
                className="input"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetails;
